import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { buttonClass } from "@/components/base/buttonStyles";
import HelpDrawer from "@/components/layout/HelpDrawer";
import NotificationPanel from "@/components/layout/NotificationPanel";
import ToastLayer from "@/components/base/ToastLayer";
import { findHelpTopic } from "@/help/content";
import useRecentScreens from "@/hooks/useRecentScreens";
import { DENSITIES, useDensity } from "@/hooks/useDensity";

/**
 * 담당자(PC 웹) 레이아웃 — 좌측 사이드바 + 본문.
 * 디자인 원칙(§9-1): 아이콘·이모지·박스 장식 없이 타이포와 여백만으로 위계를 만든다.
 * 사용자 관리 메뉴는 시스템관리자에게만 보인다(요구사항 4.1).
 *
 * 셸은 **항상 보인다**(설계원칙 P3·P13). 사이드바·상단바·머리말·필터바는 고정이고
 * 스크롤은 각 화면의 데이터 영역 한 곳에서만 일어난다 — 여백은 `Screen` 이 갖는다.
 */

interface NavGroup {
  label: string;
  items: {
    label: string;
    path: string;
    /** 하위 경로까지 활성 처리할 prefix */
    prefix?: string;
    /** 메뉴 하나가 화면 여러 개를 묶을 때 함께 활성 처리할 경로들 (예: 장소 ↔ 구간 탭) */
    alsoActiveOn?: string[];
  }[];
}

const NAV_GROUPS: NavGroup[] = [
  {
    label: "배차관리",
    items: [
      // 보는 화면과 만드는 화면을 가른다 (2026-09-09). 배차현황은 기사·업체를 한 표로
      // 보여 주는 조회 전용이고, 만들고 고치는 일은 `배차 등록` 의 두 탭에서 한다.
      //
      // `prefix` 는 **배차 등록**에 붙인다 — `/dispatch/{id}/operation`·`/edit` 에서
      // 켜져야 할 것이 그쪽이다. 형제 경로는 `isActive` 가 스스로 비켜 가므로
      // `/admin/dispatch` 정확 일치일 때 배차현황만 켜진다.
      { label: "배차 현황", path: "/admin/dispatch" },
      { label: "배차 등록", path: "/admin/dispatch/orders", prefix: "/admin/dispatch" },
    ],
  },
  {
    label: "일일실적",
    items: [
      { label: "일일실적 현황", path: "/admin/performance" },
      // 월말 마감 뒤의 확정 실적 (고객 요청 2026-10-14). 일일실적 바로 아래에 둔다 —
      // 같은 표를 하루치로 보느냐 한 달치로 보느냐의 차이라 한 묶음으로 읽혀야 한다.
      { label: "월말 실적", path: "/admin/performance/monthly" },
      { label: "MES 업로드", path: "/admin/performance/mes-upload" },
      // 하행 송장 사진을 안 받는 품목의 월말 마감 엑셀 (2026-09-16).
      // MES 업로드 바로 아래에 둔다 — 둘 다 "파일을 올려 실적을 확정하는" 자리다.
      { label: "마감 실적 업로드", path: "/admin/performance/closing-upload" },
      { label: "하행 가실적", path: "/admin/performance/downbound-estimate" },
    ],
  },
  {
    label: "송장내역확인",
    items: [
      { label: "제출현황", path: "/admin/invoice" },
      { label: "미매칭·중복", path: "/admin/invoice/unmatched" },
    ],
  },
  {
    label: "결산업무",
    items: [
      { label: "매출·매입 집계표", path: "/admin/settlement/summary" },
      { label: "거래명세서·계산서", path: "/admin/settlement" },
      { label: "통계", path: "/admin/statistics" },
    ],
  },
  {
    label: "기준정보",
    items: [
      { label: "거래처", path: "/admin/master/company" },
      // 구간·장소는 화면 둘을 탭으로 오간다. 어느 탭에 있든 이 메뉴가 '지금 여기' 여야 한다.
      // 구간이 앞이다(담당자 요청 2026-10-30) — 담당자가 날마다 여는 쪽이 구간이다.
      {
        label: "구간·장소",
        path: "/admin/master/route",
        alsoActiveOn: ["/admin/master/location"],
      },
      { label: "품목·매핑", path: "/admin/master/item" },
      { label: "차량·기사", path: "/admin/master/vehicle" },
      { label: "단가", path: "/admin/master/price" },
      { label: "월간 계획", path: "/admin/master/plan" },
      { label: "자사정보", path: "/admin/master/profile" },
    ],
  },
  {
    label: "기사관리",
    items: [
      { label: "이의신청 회신", path: "/admin/drivers/disputes" },
      { label: "차량변경 승인", path: "/admin/drivers/vehicle-changes" },
      { label: "공지 관리", path: "/admin/drivers/notices" },
      { label: "기사 계정", path: "/admin/drivers/accounts" },
    ],
  },
];

/** 시스템관리자에게만 노출하는 메뉴 */
const ADMIN_ONLY_GROUP: NavGroup = {
  label: "시스템",
  items: [
    { label: "사용자 관리", path: "/admin/users" },
    // 계정을 지울 때 "변경 이력에 그대로 남습니다" 라고 적어 온 그 이력을 보는 자리다
    { label: "변경 이력", path: "/admin/audit-logs" },
  ],
};

/**
 * 사이드바 접힘 상태 (설계원칙 P11).
 *
 * **기본값은 접힘이 아니라 펼침이다.** 데스크톱에서 내비게이션을 숨기면 사용률이 27% 로
 * 떨어진다는 것이 NN/g 의 측정값이고(노출 48% · 콤보 50%), 자동 숨김을 둘 근거는 어느
 * 자료에도 없다. 접기는 화면을 넓히고 싶은 사용자를 위한 **선택지**이고, 그래서 그 선택을 저장한다.
 *
 * 아이콘을 쓰지 않으므로(UI 규칙 1) 접힌 아이콘 레일은 만들지 않는다 — 라벨 없는 아이콘
 * 내비게이션은 그 자체로 인지 부하를 올린다는 것이 같은 문서의 결론이다. 대신 접힌 상태에서는
 * `메뉴` 로 **라벨이 그대로 보이는 오버레이**를 연다.
 */
const COLLAPSE_KEY = "hj.nav.collapsed";

/** 최근 본 화면 칩 — 넓은 화면(1536px~)에서 보일 최대 개수 */
const RECENT_MAX = 6;
/** 그보다 좁으면 여기까지만. 이름이 잘려 못 읽는 것보다 적게 보이는 편이 낫다 */
const RECENT_NARROW = 3;

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === "true";
  } catch {
    return false;
  }
}

export default function AdminLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { displayName, role, logout } = useAuth();
  const { density, setDensity } = useDensity();

  const navGroups = useMemo(
    () => (role === "시스템관리자" ? [...NAV_GROUPS, ADMIN_ONLY_GROUP] : NAV_GROUPS),
    [role]
  );

  // 도움말은 화면마다 내용이 다르므로 경로가 바뀌면 닫는다.
  const [helpOpen, setHelpOpen] = useState(false);
  const helpTopic = useMemo(() => findHelpTopic(location.pathname), [location.pathname]);
  useEffect(() => setHelpOpen(false), [location.pathname]);

  /**
   * 최근 본 화면 — 도움말이 모르는 경로는 사이드바 메뉴 라벨로 메운다.
   * `useRecentScreens` 가 콜백을 의존성으로 쓰므로 참조를 붙들어 둔다.
   */
  const navLabel = useCallback(
    (pathname: string) =>
      navGroups.flatMap((group) => group.items).find((item) => item.path === pathname)?.label,
    [navGroups]
  );
  const recent = useRecentScreens(location.pathname, location.search, navLabel);

  const [collapsed, setCollapsed] = useState(readCollapsed);
  /** 접힌 상태에서 잠시 펼쳐 보는 오버레이 */
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => setMenuOpen(false), [location.pathname]);

  const setCollapsedSaved = useCallback((value: boolean) => {
    setCollapsed(value);
    setMenuOpen(false);
    try {
      localStorage.setItem(COLLAPSE_KEY, String(value));
    } catch {
      // 저장에 실패해도 이번 세션 동안은 적용된다.
    }
  }, []);

  /**
   * `?` 로 도움말 (설계원칙 P27 — 가속기는 문맥 안에서 발견 가능해야 한다).
   * 도움말 드로어 안에 단축키 목록이 있고, 그 목록이 이 키를 알려 준다.
   * 입력 중에는 무시한다 — 검색창에 물음표를 치면 도움말이 열려서는 안 된다.
   */
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "?") return;
      const target = event.target as HTMLElement | null;
      if (!target) return;
      const tag = target.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || target.isContentEditable) return;
      event.preventDefault();
      setHelpOpen((open) => !open);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const isActive = (path: string, prefix?: string, alsoActiveOn?: string[]) => {
    if (location.pathname === path) return true;
    if (alsoActiveOn?.includes(location.pathname)) return true;
    if (!prefix) return false;
    // prefix 활성은 정확히 그 그룹의 하위 경로일 때만 (형제 메뉴와 겹치지 않게)
    const siblings = navGroups.flatMap((g) => g.items.map((i) => i.path));
    if (siblings.some((s) => s !== path && s === location.pathname)) return false;
    return location.pathname.startsWith(`${prefix}/`);
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const navList = (
    <nav className="flex-1 overflow-y-auto px-3 pb-6">
      {navGroups.map((group) => (
        <div key={group.label} className="mb-5">
          <div className="px-2 pb-1 text-2xs font-medium tracking-wider text-gray-400">
            {group.label}
          </div>
          {group.items.map((item) => {
            const active = isActive(item.path, item.prefix, item.alsoActiveOn);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`block whitespace-nowrap rounded px-2 py-1.5 text-sm transition-colors ${
                  active
                    ? "bg-primary-50 font-medium text-primary-700"
                    : "text-gray-500 hover:bg-gray-50 hover:text-gray-900"
                }`}
              >
                {item.label}
              </Link>
            );
          })}
        </div>
      ))}
    </nav>
  );

  const brand = (
    <div className="px-5 pb-5 pt-6">
      <Link to="/admin/dispatch" className="block">
        <h1 className="text-sm font-semibold tracking-tight text-gray-900">한중 상하차 관리</h1>
      </Link>
    </div>
  );

  return (
    <div className="flex h-screen bg-white">
      {!collapsed && (
        <aside className="flex w-56 shrink-0 flex-col border-r border-gray-100">
          {brand}
          {navList}
          <div className="shrink-0 border-t border-gray-100 px-3 py-3">
            <button
              type="button"
              onClick={() => setCollapsedSaved(true)}
              className={`${buttonClass("secondary", "small")} w-full`}
            >
              메뉴 접기
            </button>
          </div>
        </aside>
      )}

      {/* 접힌 상태에서 잠시 펼쳐 보는 오버레이 — 아이콘 레일 대신이다 */}
      {collapsed && menuOpen && (
        <>
          <div
            className="fixed inset-0 z-40 bg-gray-900/10"
            onClick={() => setMenuOpen(false)}
            aria-hidden="true"
          />
          <aside className="fixed left-0 top-0 z-50 flex h-screen w-56 flex-col border-r border-gray-200 bg-white shadow-[1px_0_0_0_rgba(0,0,0,0.04)]">
            {brand}
            {navList}
            <div className="shrink-0 border-t border-gray-100 px-3 py-3">
              <button
                type="button"
                onClick={() => setCollapsedSaved(false)}
                className={`${buttonClass("secondary", "small")} w-full`}
              >
                메뉴 고정
              </button>
            </div>
          </aside>
        </>
      )}

      {/*
        도움말이 열리면 본문을 드로어 폭만큼 **밀어 준다** (2026-09-15).
        드로어는 비모달이라 본문을 보며 읽으라고 만든 것인데, 오른쪽 끝 버튼들
        (`결산 확정`·`엑셀 다운로드`·표 액션)이 드로어 밑에 깔려 있었다.
        설명에 마우스를 올리면 그 조각을 짚어 주는 기능을 넣고 나서야 드러났다 —
        짚어 놓고 가려져 있으면 짚지 않은 것과 같다.
        좁은 화면(1280px 미만)에서는 밀지 않는다 — 남는 폭이 없어 표가 더 못 읽힌다.
      */}
      <main
        className={`flex flex-1 flex-col overflow-hidden transition-[padding] duration-150 ${
          helpOpen ? "xl:pr-[26rem]" : ""
        }`}
      >
        <header className="flex shrink-0 items-center gap-3 border-b border-gray-100 px-8 py-3">
          {collapsed && (
            <button
              type="button"
              onClick={() => setMenuOpen((open) => !open)}
              aria-expanded={menuOpen}
              className={`${buttonClass("secondary", "small")} ${
                menuOpen ? "border-primary-600 bg-primary-50 text-primary-700" : ""
              }`}
            >
              메뉴
            </button>
          )}

          {/*
            방금까지 보던 화면 (담당자 요청 2026-09-09).

            *"기사배차 입력하다가 잠깐 다른 화면 보고 싶어서 갔다 오고 싶다"* — 돌아오는 길을
            사이드바에서 다시 찾지 않게 한다. 쓰던 조회 조건과 등록 폼은 `useStickyState` 가
            들고 있으므로, 여기서 돌아오면 하던 자리 그대로다.

            **줄을 새로 만들지 않는다.** 상단바 안에 두면 표가 보는 높이가 그대로다.

            **몇 개를 보일지는 화면 폭이 정한다.** 오른쪽 그룹(밀도·도움말·알림·이름·로그아웃)이
            1280px 노트북에서 절반을 먹으므로 거기서 6개를 늘어놓으면 이름이 서너 글자로 잘려
            무슨 화면인지 못 읽는다. 넓은 화면(1536px~)에서만 6개까지 펴고 좁으면 3개로 둔다.

            생김새는 필터 칩과 같다(`FilterBar`) — 이 앱에서 칩은 곧 작은 보조 버튼이다.
          */}
          {recent.length > 0 && (
            <nav aria-label="최근 본 화면" className="flex min-w-0 items-center gap-2">
              <span className="shrink-0 text-xs text-gray-400">최근</span>
              {recent.slice(0, RECENT_MAX).map((screen, index) => (
                <Link
                  key={screen.path}
                  to={screen.href}
                  title={screen.title}
                  className={`${buttonClass("secondary", "small")} max-w-[9rem] truncate ${
                    index >= RECENT_NARROW ? "hidden 2xl:inline-flex" : ""
                  }`}
                >
                  {screen.title}
                </Link>
              ))}
            </nav>
          )}

          {/*
            표 밀도 — 애플리케이션에 하나뿐이다 (설계원칙 P1).
            표마다 두면 한 페이지 안에서 밀도가 섞이고, 그것이 Fiori 가 금지하는 상태다.
            고른 값은 "지금 여기/고른 것" 표시라 주색 면이 아니라 주색 테두리로 둔다 —
            주색 면은 그 화면의 주 동작 버튼 몫이다 (CLAUDE.md §주색).
          */}
          <div className="ml-auto flex items-center gap-1" role="group" aria-label="표 밀도">
            {DENSITIES.map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => setDensity(option)}
                aria-pressed={density === option}
                className={`${buttonClass("secondary", "small")} ${
                  density === option ? "border-primary-600 bg-primary-50 text-primary-700" : ""
                }`}
              >
                {option}
              </button>
            ))}
          </div>

          <button
            type="button"
            onClick={() => setHelpOpen((open) => !open)}
            aria-expanded={helpOpen}
            className={`${buttonClass("secondary", "small")} ${
              helpOpen ? "border-primary-600 bg-primary-50 text-primary-700" : ""
            }`}
          >
            ? 도움말
          </button>
          <NotificationPanel />
          <span className="ml-1 text-sm text-gray-900">{displayName}</span>
          {role && <span className="text-xs text-gray-400">{role}</span>}
          <button
            type="button"
            onClick={handleLogout}
            className={buttonClass("secondary", "small")}
          >
            로그아웃
          </button>
        </header>

        {/* 여백은 각 화면의 `Screen` 이 갖는다 — 여기서 주면 고정 영역까지 함께 스크롤된다 */}
        <div className="min-h-0 flex-1 overflow-hidden">
          <Outlet />
        </div>
      </main>

      <HelpDrawer topic={helpTopic} open={helpOpen} onClose={() => setHelpOpen(false)} />
      {/* 누른 결과는 흐름 밖에 띄운다 — 표에서 49px 을 빼지 않는다 (2026-10-02) */}
      <ToastLayer />
    </div>
  );
}
